import type { Restaurant } from '@shared/types/restaurant';
import { roundMoney } from '@shared/utils/money';
import type { BookingContextValue } from '../state/types';
import { activeSlotMenuFor } from './activeSlotMenu';
import { calculatePairingsFee, type FeeBreakdown } from './calculateFee';

/** An ARS venue that doesn't settle in USD: its foreign cards are charged in USD. */
export function chargesForeignCardsInUsd(restaurant: Restaurant): boolean {
  return (restaurant.currency ?? '').toUpperCase() === 'ARS' && !restaurant.settleUsd;
}

export interface UsdBill {
  /** The guarantee: menu (or slot) USD amount × guests. */
  base: number;
  pairings: number;
  gratuity: number;
  salesTax: number;
  total: number;
}

/**
 * The USD a card issued outside Argentina is charged for this deposit, built the
 * way the backend builds `feeUSDDeposit`: the USD guarantee (kids at their USD
 * amount when set, else at the adult amount), USD pairings, then tip, then tax.
 * Null when the note does not apply or there is no USD amount to state.
 */
export function foreignCardUsdTotal(
  booking: BookingContextValue,
  restaurant: Restaurant,
  breakdown: FeeBreakdown,
): number | null {
  return foreignCardUsdBill(booking, restaurant, breakdown)?.total ?? null;
}

/** The same USD amount, itemized the way the local bill is. */
export function foreignCardUsdBill(
  booking: BookingContextValue,
  restaurant: Restaurant,
  breakdown: FeeBreakdown,
): UsdBill | null {
  if (!chargesForeignCardsInUsd(restaurant)) return null;
  if (breakdown.guaranteeType !== 'deposit' || !(breakdown.total > 0)) return null;
  // A checkout surcharge is charged on the USD amount on one production path and not on the other: state nothing.
  if (breakdown.serviceFee > 0) return null;

  const slot = booking.selectedTime && booking.timeSlots
    ? booking.timeSlots.find((s) => s.hour === booking.selectedTime)
    : null;
  const menu = activeSlotMenuFor(booking, restaurant.publicSearchMode);

  let unitUsd = 0;
  let kidsUnitUsd = 0;
  if (breakdown.guaranteeSource === 'menu') {
    unitUsd = menu?.guarantee?.feeUSD ?? 0;
    kidsUnitUsd = menu?.guarantee?.feeKidsUSD ?? 0;
  } else if (breakdown.guaranteeSource === 'slot') {
    // `slot.feeUSD` is already multiplied by the party; `allFees.depositUSD` is the per-person amount.
    unitUsd = slot?.allFees?.depositUSD ?? 0;
    // The slot payload carries no USD kids amount, so the USD a party with kids pays is unknown here.
    if (booking.kids && booking.howManyKids > 0) return null;
  }
  if (!(unitUsd > 0)) return null;

  const party = booking.diners;
  const kids = booking.kids ? Math.min(booking.howManyKids, party) : 0;
  let base: number;
  if (!breakdown.perPerson) {
    base = unitUsd;
  } else if (kids > 0 && kidsUnitUsd > 0) {
    base = unitUsd * (party - kids) + kidsUnitUsd * kids;
  } else {
    base = unitUsd * party;
  }

  const pairings = calculatePairingsFee(booking.selectedPairings, true);
  const subtotal = base + pairings;
  const tipPercent = breakdown.gratuity > 0 ? breakdown.gratuityPercent : 0;
  const taxPercent = breakdown.salesTax > 0 ? breakdown.salesTaxPercent : 0;
  // No menu tax: the backend's USD charge (Reservation::calculateFeeUSD) has none.
  const gratuity = subtotal * tipPercent / 100;
  const salesTax = (subtotal + gratuity) * taxPercent / 100;
  return {
    base: roundMoney(base),
    pairings: roundMoney(pairings),
    gratuity: roundMoney(gratuity),
    salesTax: roundMoney(salesTax),
    total: roundMoney(subtotal + gratuity + salesTax),
  };
}
